import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ReportsService } from '@/services/reports.service'
import type { Report, ReportCreatePayload} from '@/entities/report'
import NotificationMessageService from '@/services/notificationmessage.service'

const service = new ReportsService(undefined, 'supportingFiles')

export const useSupportingFilesStore = defineStore('supportingFiles', () => {
  // --- State ---
  const reports = ref<Report[]>([])
  const report = ref<Report | null>(null)
  const loadedValue = ref<number | null>(null)

  // --- Getters ---
  const getReports = computed(() => reports.value)
  const getReport = computed(() => report.value)
  const getLoadedValue = computed(() => loadedValue.value)
  const getSupportingFiles = computed(() => reports.value)

  // --- Actions ---
  async function fetchOrderReports(orderId: number) {
    try {
      const result = await service.getReports(orderId)
      reports.value = result ?? []
      return result
    } catch (err) {
      console.warn(`Error while fetching reports`, err)
    }
  }

  async function fetchOrderReport(fetchData: {
    orderId: number
    orderFileId: number
    fileName: string
  }) {
    if (fetchData?.orderId > 0 && fetchData?.orderFileId > 0) {
      try {
        await service.getReport(fetchData.orderId, fetchData.orderFileId, fetchData.fileName)
      } catch (e) {
        NotificationMessageService.showError('Error while loading report')
        console.warn(
          `Error while fetching file ID ${fetchData.orderFileId} of order ${fetchData.orderId}`,
        )
      }
    }
  }

  async function deleteReport(orderId: number, orderFileId: number): Promise<boolean> {
    if (orderId > 0 && orderFileId > 0) {
      try {
        const err = await service.deleteReport(orderId, orderFileId)
        return !err
      } catch (e) {
        console.warn(`Error while deleting file ID ${orderFileId} of order ${orderId}`)
      }
    }
    return false
  }

  async function insertReport(orderId: number, newReport: ReportCreatePayload): Promise<boolean> {
    if (orderId > 0 && newReport) {
      try {
        const err = await service.insertReport(orderId, newReport)
        return !err
      } catch (e) {
        console.warn(`Error while inserting report of order ${orderId}`)
      }
    }
    return false
  }

  function fetchLoadedValue() {
    loadedValue.value = service.getLoadedValue() ?? null
  }

  return {
    // State
    reports,
    report,
    loadedValue,

    // Getters
    getReports,
    getReport,
    getLoadedValue,
    getSupportingFiles,

    // Actions
    fetchOrderReports,
    fetchOrderReport,
    deleteReport,
    insertReport,
    fetchLoadedValue,
  }
})
